import React, { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { Click, Flex, Spacer, Text, Toggle, View } from 'vcc-ui';
import { sideNavigation } from '../docs.config';

const Link = ({ children, href }) => {
  const router = useRouter();
  return (
    <Text
      as={Click}
      subStyle="inline-link"
      aria-current={href === router.pathname ? 'page' : undefined}
      extend={({ theme: { baselineSubGrid, color } }) => ({
        padding: '0.25rem 0.75rem',
        textDecoration: 'none',
        transition: 'all 130ms ease-out',
        borderRadius: 2,
        '&:hover': {
          background: color.ornament.divider,
        },
        '&[aria-current=page]': {
          background: color.ornament.divider,
        },
      })}
      href={href}
    >
      {children}
    </Text>
  );
};

const RenderItems = () => {
  return Object.keys(sideNavigation).map((key) => (
    <Flex
      extend={{
        flexShrink: 0,
        maxWidth: '24rem',
      }}
      key={key}
    >
      <Text
        as="h2"
        variant="columbus"
        subStyle="emphasis"
        key={key}
        extend={({ theme: { baselineSubGrid } }) => ({
          padding: `${baselineSubGrid}px ${3 * baselineSubGrid}px`,
        })}
      >
        {key}
      </Text>
      <Spacer />
      {typeof sideNavigation[key] === 'string' ? (
        <Link key={key} href={sideNavigation[key]}>
          {key}
        </Link>
      ) : Array.isArray(sideNavigation[key]) ? (
        sideNavigation[key].map((item) => (
          <Link key={item[0]} href={item[1]}>
            {item[0]}
          </Link>
        ))
      ) : (
        Object.keys(sideNavigation[key]).map((item) => (
          <Flex key={item}>
            <Spacer />
            <Text
              as="h3"
              variant="kelly"
              subStyle="emphasis"
              extend={({ theme: { baselineSubGrid } }) => ({
                padding: `${baselineSubGrid}px ${3 * baselineSubGrid}px`,
              })}
            >
              {item}
            </Text>
            {sideNavigation[key][item].map((subItem) => (
              <Link key={subItem[1]} href={subItem[1]}>
                {subItem[0]}
              </Link>
            ))}
          </Flex>
        ))
      )}
      <Spacer size={4} />
    </Flex>
  ));
};

const DocsNavigation = ({ mobileNavIsOpen, currentTheme, setCurrentTheme }) => {
  const navRef = useRef();
  const router = useRouter();
  useEffect(() => {
    if (navRef.current?.scrollIntoView) {
      navRef.current
        ?.querySelector('[aria-current=page]')
        ?.parentNode.scrollIntoView();
    }
  }, [router.asPath, mobileNavIsOpen]);
  return (
    <div>
      <View
        as="nav"
        ref={navRef}
        direction="column"
        extend={({ theme }) => ({
          position: 'sticky',
          marginLeft: '-0.75rem',
          marginRight: '-0.75rem',
          top: 'var(--topbar-height)',
          scrollPaddingTop: 8,
          overflowY: 'auto',
          height: 'calc(100vh - var(--topbar-height))',
          [theme.breakpoints.untilL]: {
            display: mobileNavIsOpen ? 'flex' : 'none',
            top: `calc(var(--topbar-height) * 2)`,
            height: 'calc(100vh - var(--topbar-height) * 2)',
            position: 'fixed',
            background: theme.color.background.primary,
            zIndex: 'calc(var(--topbar-z-index) - 1)',
            left: '1.5rem',
            right: '1.5rem',
          },
        })}
      >
        <Spacer size={{ default: 2, fromL: 6 }} />
        <RenderItems />
        <View direction="row" paddingLeft={1.5}>
          <Toggle
            aria-label="Toggle theme"
            checked={currentTheme === 'dark'}
            onChange={() =>
              setCurrentTheme(currentTheme === 'dark' ? 'light' : 'dark')
            }
          />
          <Spacer />
          <Text>Toggle theme</Text>
        </View>
        <Spacer size={12} />
      </View>
    </div>
  );
};

export default DocsNavigation;
